<template>
  <div
    ref="$el"
    class="accordion-item"
    :class="props.theme"
  >
    <div
      class="head"
      @click="toggle"
    >
      <UiComponentHeading
        :as="props.as"
        :level="3"
      >
        {{ props.title }}
      </UiComponentHeading>
      <div class="circle">
        <div class="line" />
        <div class="line" />
      </div>
    </div>
    <div
      class="body"
    >
      <div
        class="content"
        v-html="props.content"
      />
    </div>
  </div>
</template>

<script setup>
// Import
import { gsap } from 'gsap'

// Emitter
const emit = defineEmits([
  'item:toggle',
])

// Props
const props = defineProps({
  as: {
    default() {
      return 'heading'
    },
    required: false,
    type: String,
    validator(value) {
      return ['heading', 'paragraph'].indexOf(value) !== -1
    },
  },
  content: {
    required: true,
    type: String,
  },
  title: {
    required: true,
    type: String,
  },
  theme: {
    default() {
      return 'light'
    },
    required: false,
    type: String,
    validator(value) {
      return ['dark', 'light'].indexOf(value) !== -1
    },
  },
})

// Refs
const $el = ref(null)
const opened = ref(false)

// Variable
let timeline = null

// Watchers
watch(
  () => opened.value,
  (value) => {
    const playOrReverse = value ? 'play' : 'reverse'
    timeline[playOrReverse]()
  },
)

// Methods
function close() {
  opened.value = false
}

function createTimeline() {
  timeline = new gsap.timeline()
  timeline.from($el.value.querySelector('.body'), {
    duration: 0.8,
    ease: 'expo.inOut',
    height: 0,
  }, 0)
  timeline.to($el.value.querySelectorAll('.line')[1], {
    duration: 0.8,
    ease: 'expo.inOut',
    scale: 0,
  }, 0)
  timeline.pause()
}

function toggle() {
  opened.value = !opened.value
  emit('item:toggle')
}

// Lifecycle Hooks
onBeforeUnmount(() => {
  timeline.kill()
  timeline = null
})

onMounted(() => {
  createTimeline()
})

// Expose
defineExpose({
  close,
  toggle,
})
</script>

<style lang="postcss" scoped>
.accordion-item {
  @apply flex flex-col w-full;

  &.dark {
    .head {
      .h3 {
        @apply text-white;
      }

      .circle {
        @apply bg-[#333D41];

        .line {
          &:first-child,
          &:last-child {
            @apply bg-white;
          }
        }
      }
    }

    .body {
      .content {
        @apply text-[#CED0D0];

        :deep(.h5) {
          @apply text-white;
        }

        :deep(b),
        :deep(p),
        :deep(strong) {
          @apply text-[#CED0D0];
        }

        :deep(a) {
          @apply text-white;
        }

        :deep(li) {
          &::before {
            @apply text-[#CED0D0];
          }
        }
      }
    }
  }

  &.light {
    .head {
      .h3 {
        @apply text-winter-green-900;
      }

      .circle {
        @apply bg-grey-blue-100;

        .line {
          &:first-child,
          &:last-child {
            @apply bg-winter-green-900;
          }
        }
      }
    }

    .body {
      .content {
        @apply text-grey-blue-600;

        :deep(.h5) {
          @apply text-winter-green-900;
        }

        :deep(b),
        :deep(p),
        :deep(strong) {
          @apply text-grey-blue-600;
        }

        :deep(a) {
          @apply text-livestorm-blue-700;
        }

        :deep(li) {
          &::before {
            @apply text-livestorm-blue-700;
          }
        }
      }
    }
  }

  .head {
    @apply flex flex-row justify-between items-center select-none cursor-pointer;

    .h3 {
      @apply font-medium text-origin leading-7 !important;
      width: calc(100% - 64px);

      @screen mdminus {
        @apply text-xl !important;
      }

      @screen mdplus {
        @apply text-xl !important;
      }
    }

    .circle {
      @apply relative flex ml-8 w-8 h-8 rounded-full;

      .line {
        &:first-child {
          @apply absolute left-[10px] top-[15.5px] flex h-px w-3;
        }

        &:last-child {
          @apply absolute left-[15.5px] top-[10px] flex h-3 w-px;
        }
      }
    }
  }

  .body {
    @apply overflow-hidden;

    .content {
      @apply pt-8;

      :deep(.h5 + p) {
        @apply mt-2;
      }

      :deep(p + .h5) {
        @apply mt-4;
      }

      :deep(p) {
        @apply select-none;
      }

      :deep(p + *),
      :deep(ul + *),
      :deep(ol + *) {
        @apply mt-4;
      }

      :deep(li) {
        &::before {
          @apply content-['•'] mr-2 font-bold;
        }
      }
    }
  }
}
</style>
